import React, { useEffect, useRef } from 'react';
import type { AuditLog } from '../../../models/auditLog';
import { formatUtcDateTime } from '../../../shared/utils/formatDate';

export interface AuditLogDetailModalProps {
  entry: AuditLog | null;
  onClose: () => void;
}

function formatMetadata(metadataJson: string | null): string {
  if (!metadataJson) {
    return '—';
  }
  try {
    return JSON.stringify(JSON.parse(metadataJson), null, 2);
  } catch {
    return metadataJson;
  }
}

/** Read-only variant of the shared modal shell (see ConfirmModal) — no confirm action, just detail. */
export function AuditLogDetailModal({ entry, onClose }: AuditLogDetailModalProps): React.ReactElement | null {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!entry) {
      return;
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    panelRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [entry, onClose]);

  if (!entry) {
    return null;
  }

  return (
    <div className="bo-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        ref={panelRef}
        className="bo-modal-panel"
        role="dialog"
        aria-modal
        aria-labelledby="bo-audit-detail-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="bo-audit-detail-title" className="bo-modal-title">
          Audit entry
        </h2>
        <div className="bo-detail-fields-grid">
          <div className="bo-detail-field">
            <span className="bo-detail-field-label">Timestamp (UTC)</span>
            <span className="bo-detail-field-value">{formatUtcDateTime(entry.createdAt)}</span>
          </div>
          <div className="bo-detail-field">
            <span className="bo-detail-field-label">Actor</span>
            <span className="bo-detail-field-value">
              {entry.actorEmail || '—'}
              {entry.actorId ? ` (${entry.actorId})` : ''}
            </span>
          </div>
          <div className="bo-detail-field">
            <span className="bo-detail-field-label">Action type</span>
            <span className="bo-detail-field-value">{entry.actionType}</span>
          </div>
          <div className="bo-detail-field">
            <span className="bo-detail-field-label">Target</span>
            <span className="bo-detail-field-value">
              {entry.targetType ?? '—'}
              {entry.targetName ? ` — ${entry.targetName}` : entry.targetId ? ` — ${entry.targetId}` : ''}
            </span>
          </div>
          <div className="bo-detail-field">
            <span className="bo-detail-field-label">Metadata</span>
            <span className="bo-detail-field-value">
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {formatMetadata(entry.metadataJson)}
              </pre>
            </span>
          </div>
        </div>
        <div className="bo-actions" style={{ marginTop: '1.25rem', justifyContent: 'flex-end' }}>
          <button type="button" className="bo-btn bo-btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
